<template>
    <div class="client-specific-fields-changes-viewer">
        <div class="client-specific-fields-changes-viewer__grid">
            <promo-ag-grid
                ref="agGrid"
                :row-data="rowData"
                :column-defs="columnDefs"
                :grid-options="gridOptions"
                :default-col-def="defaultColDef"
                :grid-style="gridStyle"
                :grid-class="gridClass"
                dom-layout="autoHeight"
            />
        </div>
        <div class="client-specific-fields-changes-viewer__actions" />
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { get, isEmpty } from 'lodash';
import { mapState } from 'vuex';
import parentChildTypes from '@enums/parent-child';
import { toSentenceCase } from '@/js/utils/string-utils';
import ButtonField from '@/js/components/promo-ag-grid/ag-button-field.vue';

const parentChangesLocalizationKey = 'planning.parentChanges';
const acceptCellClasses = ['parent-child', 'parent-child__button'];
const acceptCellType = 'parentChildButton';

export default {
    /* eslint-disable vue/no-unused-components */
    components: {
        ButtonField,
    },
    props: {
        // Child client specific fields
        child: {
            type: Object,
            default: null,
        },
        // Parent changeset client specific fields
        parent: {
            type: Object,
            default: null,
        },
        selectAllChild: {
            type: Boolean,
            default: false,
        },
        selectAllParent: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select-client-specific-fields'],
    data() {
        return {
            defaultColDef: {
                editable: false,
                suppressMovable: true, // Stop users from being able to rearrange columns.
                lockPinned: true, // Stop users from being able to pin columns.
                filter: true,
                menuTabs: [],
            },
            gridOptions: markRaw({
                suppressContextMenu: true, // only show filter
                headerHeight: 40,
                rowHeight: 35, // Specified in pixels.
                columnTypes: {
                    parentChildButton: {
                        headerName: '',
                        headerHeight: 0,
                        filter: false,
                        maxWidth: 70,
                        cellRenderer: ButtonField,
                        cellRendererParams: {
                            onClick: params => {
                                this.selectChanges(params.value);
                            },
                            visible: params => params.node?.rowIndex === 0,
                        },
                    },
                },
            }),
            gridStyle: 'width: 144rem; height: 100%;',
            gridClass: 'ag-theme-custom__parent-child-changes',
            headerColumns: [
                parentChildTypes.child,
                parentChildTypes.parent,
                parentChildTypes.accept,
            ],
            fieldName: 'offerCommercialField',
            selectedChangeset: parentChildTypes.parent,
            acceptChildButtonText: toSentenceCase(
                this.$tkey(`${parentChangesLocalizationKey}.gridHeadings.${parentChildTypes.child}`)
            ),
            acceptParentButtonText: toSentenceCase(
                this.$tkey(
                    `${parentChangesLocalizationKey}.gridHeadings.${parentChildTypes.parent}`
                )
            ),
        };
    },
    computed: {
        ...mapState('clientConfig', ['offerCommercialFields']),
        offerCommercialFieldsMap() {
            return this.offerCommercialFields.reduce((acc, { field, translationKey }) => {
                acc[field] = translationKey;
                return acc;
            }, {});
        },
        rowData() {
            if (!this.parent || isEmpty(this.parent)) {
                return [];
            }
            const offerClientSpecificFieldsRow = this.headerColumns.reduce((acc, header) => {
                // Resolve source explicitly to avoid accessing undefined component properties
                // (for example `this.accept`) which triggers Vue 3 runtime warnings.
                let fieldValue;
                if (header === parentChildTypes.accept) {
                    fieldValue = { offerCommercialField: null, couponBarcode: null };
                } else {
                    let source = null;
                    if (header === parentChildTypes.child) {
                        source = this.child;
                    } else if (header === parentChildTypes.parent) {
                        source = this.parent;
                    }

                    fieldValue = {
                        offerCommercialField: get(source, 'offerCommercialField'),
                        couponBarcode: get(source, 'couponBarcode'),
                    };
                }

                acc[`${header}`] = {
                    fieldName: this.fieldName,
                    fieldValue,
                };
                return acc;
            }, {});

            return [offerClientSpecificFieldsRow];
        },

        columnDefs() {
            const checkIfAcceptButtonSelected = params =>
                this.selectedChangeset === params.colDef.cellRendererParams.value;

            return this.headerColumns.map(header => {
                const children = [];

                if (header === parentChildTypes.accept) {
                    children.push(
                        {
                            fieldName: 'childButton',
                            type: acceptCellType,
                            cellClass: [...acceptCellClasses, 'parent-child__button--child'],
                            cellClassRules: {
                                'parent-child__button--child-selected': checkIfAcceptButtonSelected,
                            },
                            cellRendererParams: {
                                text: this.acceptChildButtonText,
                                value: parentChildTypes.child,
                            },
                        },
                        {
                            fieldName: 'parentButton',
                            type: acceptCellType,
                            cellClass: [...acceptCellClasses, 'parent-child__button--parent'],
                            cellClassRules: {
                                'parent-child__button--parent-selected': checkIfAcceptButtonSelected,
                            },
                            cellRendererParams: {
                                text: this.acceptParentButtonText,
                                value: parentChildTypes.parent,
                            },
                        }
                    );
                } else {
                    const commonCellsOptions = {
                        headerName: '',
                        headerHeight: 0,
                        cellClass: ['parent-child', `border-left__${header}`],
                        cellClassRules: {
                            'parent-child--selected': () => this.selectedChangeset === header,
                            'parent-child--not-selected': () => this.selectedChangeset !== header,
                        },
                    };

                    children.push(
                        {
                            ...commonCellsOptions,
                            field: `${header}.fieldName`,
                            maxWidth: 200,
                            cellRenderer: params => {
                                const cellText = this.$tkey(
                                    `${parentChangesLocalizationKey}.gridFields.offerClientSpecificFields.${
                                        params.value
                                    }`
                                );
                                return `<label>${toSentenceCase(cellText)}</label>`;
                            },
                        },
                        {
                            ...commonCellsOptions,
                            field: `${header}.fieldValue`,
                            maxWidth: 450,
                            autoHeight: true,
                            cellRenderer: params => {
                                const offerCommercialFieldValue = params.value.offerCommercialField
                                    ? this.$tkey(
                                          this.offerCommercialFieldsMap[
                                              params.value.offerCommercialField
                                          ]
                                      )
                                    : '-';
                                const couponBarcode = params.value.couponBarcode || '-';
                                return `
                                    <div>${toSentenceCase(
                                        this.$tkey(
                                            `${parentChangesLocalizationKey}.gridFields.offerClientSpecificFields.offerCommercialField`
                                        )
                                    )}: ${offerCommercialFieldValue}</div>
                                    <div>${toSentenceCase(
                                        this.$tkey(
                                            `${parentChangesLocalizationKey}.gridFields.restrictions.coupon`
                                        )
                                    )} ${couponBarcode}</div>
                                `;
                            },
                        }
                    );
                }

                return {
                    headerName: toSentenceCase(
                        this.$tkey(`${parentChangesLocalizationKey}.gridHeadings.${header}`)
                    ),
                    headerClass: `border-left__${header}`,
                    minWidth: 410,
                    autoHeight: true,
                    children,
                };
            });
        },
    },
    watch: {
        selectAllChild() {
            this.toggleSelection(parentChildTypes.child);
        },
        selectAllParent() {
            this.toggleSelection(parentChildTypes.parent);
        },
    },
    methods: {
        selectChanges(value) {
            this.selectedChangeset = value;
            this.$emit(
                'select-client-specific-fields',
                this.selectedChangeset === parentChildTypes.parent ? this.parent : null
            );
        },

        toggleSelection(selectedChangeset) {
            this.selectChanges(parentChildTypes[`${selectedChangeset}`]);
            if (this.$refs?.agGrid) {
                // Timeout is needed to wait for the DOM to update with the new selected state, which affects button styling and thus the element that needs to be focused.
                this.$refs.agGrid.gridApi.refreshCells();
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.client-specific-fields-changes-viewer {
    @include flex-column;
    margin-bottom: 2rem;

    &__actions {
        width: 100%;
        height: 2rem;
        background-color: $promo-white;
    }
}

.client-specific-fields-changes-viewer {
    :deep(.ag-root-wrapper) {
        min-height: auto;
    }
    :deep(.ag-header-container),
    :deep(.ag-header),
    :deep(.ag-header-viewport) {
        height: 4rem !important;
        min-height: 4rem !important;
    }

    :deep(.ag-center-cols-clipper) {
        border-top: solid 0.1rem $promo-table-blue-bg-colour !important;
    }

    :deep(.ag-cell) {
        display: block;
        line-height: 4rem;
    }

    :deep(.ag-cell-wrapper) {
        height: auto;
    }
}
</style>
